import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertTriangle, Package, TrendingDown, AlertOctagon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const HCMDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [districtDelivery, setDistrictDelivery] = useState([]);
  const [supplyChain, setSupplyChain] = useState(null);
  const [zeroSaleDistricts, setZeroSaleDistricts] = useState(null);
  const [topBottom, setTopBottom] = useState(null);
  const [perfDistribution, setPerfDistribution] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedDistrict]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [overviewRes, deliveryRes, supplyRes, zeroSaleRes, topBottomRes, perfRes, districtsRes] = await Promise.all([
        axios.get(`${API}/api/dashboards/hcm/overview`),
        axios.get(`${API}/api/dashboards/hcm/district-delivery?limit=50`),
        axios.get(`${API}/api/dashboards/hcm/supply-chain-analysis`),
        axios.get(`${API}/api/dashboards/hcm/zero-sale-districts`),
        axios.get(`${API}/api/dashboards/hcm/top-bottom?limit=5`),
        axios.get(`${API}/api/dashboards/hcm/performance-distribution`),
        axios.get(`${API}/api/dashboards/hcm/districts`)
      ]);

      setOverview(overviewRes.data);
      setDistrictDelivery(deliveryRes.data);
      setSupplyChain(supplyRes.data);
      setZeroSaleDistricts(zeroSaleRes.data);
      setTopBottom(topBottomRes.data);
      setPerfDistribution(perfRes.data);
      setDistricts(districtsRes.data);
    } catch (error) {
      console.error('Error fetching HCM dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceColor = (pct) => {
    if (pct >= 80) return '#22c55e';
    if (pct >= 50) return '#facc15';
    if (pct > 0) return '#f97316';
    return '#ef4444';
  };

  const KPICard = ({ title, value, subtitle, icon: Icon, critical }) => (
    <Card className={critical ? 'border-2 border-red-500 bg-red-50' : ''}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-full ${critical ? 'bg-red-200' : 'bg-blue-100'}`}>
            <Icon className={`w-6 h-6 ${critical ? 'text-red-700' : 'text-blue-600'}`} />
          </div>
        </div>
        <h3 className="text-sm font-medium text-gray-600 mb-2">{title}</h3>
        <p className={`text-3xl font-bold ${critical ? 'text-red-700' : ''}`}>{value}</p>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        {critical && <p className="text-xs text-red-700 mt-2 font-semibold">🚨 EMERGENCY</p>}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading HCM dashboard data...</p>
        </div>
      </div>
    );
  }

  // Prepare supply chain funnel data
  const supplyChainData = [
    { 
      name: 'Allocated', 
      value: supplyChain?.supply_chain?.allocated || 0, 
      pct: 100, 
      color: '#94a3b8' 
    },
    { 
      name: 'Issued', 
      value: supplyChain?.supply_chain?.issued || 0, 
      pct: supplyChain?.efficiency?.issue_rate || 0, 
      color: '#3b82f6' 
    },
    { 
      name: 'Sale', 
      value: supplyChain?.supply_chain?.sale || 0, 
      pct: supplyChain?.efficiency?.overall_efficiency || 0, 
      color: '#22c55e' 
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 pt-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Hot Cooked Meal & Fortified Rice Dashboard</h1>
          <p className="text-gray-600">Monitor fortified rice distribution for hot cooked meals across {overview?.total_anganwadis?.toLocaleString()} AWCs in {overview?.total_districts} districts</p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4">
          <Select value={selectedDistrict} onValueChange={setSelectedDistrict}>
            <SelectTrigger className="w-64 bg-white">
              <SelectValue placeholder="Select District" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Districts</SelectItem>
              {districts.map(district => (
                <SelectItem key={district} value={district}>{district}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            title="Total Allocated"
            value={`${(overview?.total_allocated / 1000000).toFixed(2)}M kg`}
            subtitle={`Across ${overview?.total_districts} districts`}
            icon={Package}
          />
          <KPICard
            title="Total Issued"
            value={`${(overview?.total_issued / 1000).toFixed(1)}K kg`}
            subtitle={`Issue Rate: ${overview?.issue_rate}%`}
            icon={TrendingDown}
          />
          <KPICard
            title="Permit Generated"
            value={`${(supplyChain?.supply_chain?.permit / 1000).toFixed(1)}K kg`}
            subtitle="Authorization completed"
            icon={Package}
          />
          <KPICard
            title="Anganwadi Centers"
            value={overview?.total_anganwadis?.toLocaleString()}
            subtitle="Operational AWCs"
            icon={AlertTriangle}
          />
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white">
            <TabsTrigger value="overview">Supply Chain Overview</TabsTrigger>
            <TabsTrigger value="districts">District Analysis</TabsTrigger>
            <TabsTrigger value="performance">Performance Distribution</TabsTrigger>
            <TabsTrigger value="data">Data Table</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Supply Chain Flow */}
              <Card>
                <CardHeader>
                  <CardTitle>Distribution Flow Analysis</CardTitle>
                  <CardDescription>Fortified rice allocation to distribution</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={supplyChainData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={100} />
                      <Tooltip 
                        formatter={(value, name, props) => [
                          `${value.toLocaleString()} kg (${props.payload.pct}%)`,
                          props.payload.name
                        ]}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {supplyChainData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  
                  {/* Distribution Metrics */}
                  <div className="mt-4 space-y-2">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Total Allocated</span>
                        <span className="text-lg font-bold text-blue-600">
                          {supplyChain?.supply_chain?.allocated?.toLocaleString()} kg
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Initial allocation for {overview?.total_districts} districts</p>
                    </div>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Total Issued</span>
                        <span className="text-lg font-bold text-blue-600">
                          {supplyChain?.supply_chain?.issued?.toLocaleString()} kg
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Issue efficiency: {supplyChain?.efficiency?.issue_rate}%</p>
                    </div>
                    <div className="p-3 bg-green-50 border border-green-200 rounded">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-bold">Permit Authorized</span>
                        <span className="text-xl font-bold text-green-600">
                          {supplyChain?.supply_chain?.permit?.toLocaleString()} kg
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Distribution authorization processed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Efficiency Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle>Efficiency Metrics</CardTitle>
                  <CardDescription>Distribution effectiveness by stage</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">Allocation → Issue Rate</span>
                        <span className="text-2xl font-bold text-blue-600">{supplyChain?.efficiency?.issue_rate}%</span>
                      </div>
                      <div className="w-full bg-white rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${supplyChain?.efficiency?.issue_rate}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">Overall Efficiency</span>
                        <span className="text-2xl font-bold text-green-600">{supplyChain?.efficiency?.overall_efficiency}%</span>
                      </div>
                      <div className="w-full bg-white rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full" 
                          style={{ width: `${supplyChain?.efficiency?.overall_efficiency}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 mt-6">
                      <div className="text-center p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-600 mb-1">Districts</p>
                        <p className="text-xl font-bold text-gray-900">{overview?.total_districts}</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-600 mb-1">AWCs</p>
                        <p className="text-xl font-bold text-gray-900">{overview?.total_anganwadis?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border">
                        <p className="text-xs text-gray-600 mb-1">Avg Issue Rate</p>
                        <p className="text-xl font-bold text-gray-900">{supplyChain?.efficiency?.issue_rate}%</p>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Distribution Gaps</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Allocated → Issued Gap:</span>
                          <span className="font-medium">{supplyChain?.gaps?.allocated_to_issued?.toLocaleString()} kg</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Issued → Sale Gap:</span>
                          <span className="font-medium">{supplyChain?.gaps?.issued_to_sale?.toLocaleString()} kg</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Districts Tab */}
          <TabsContent value="districts">
            <Card>
              <CardHeader>
                <CardTitle>Detailed Supply Chain Analysis</CardTitle>
                <CardDescription>Stage-wise efficiency metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="p-4 border rounded-lg">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Stage 1: Allocation</h3>
                    <p className="text-2xl font-bold">{supplyChain?.supply_chain?.allocated?.toLocaleString()} kg</p>
                    <p className="text-sm text-gray-500 mt-1">Allocated to {overview?.total_districts} districts</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Stage 2: Permit Issued</h3>
                    <p className="text-2xl font-bold">{supplyChain?.supply_chain?.permit?.toLocaleString()} kg</p>
                    <p className="text-sm text-gray-500 mt-1">Permits generated</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Stage 3: Quantity Issued</h3>
                    <p className="text-2xl font-bold">{supplyChain?.supply_chain?.issued?.toLocaleString()} kg</p>
                    <p className="text-sm text-red-600 mt-1">{supplyChain?.efficiency?.issue_rate}% issue rate</p>
                  </div>
                </div>

                <div className="p-6 bg-red-50 border-2 border-red-500 rounded-lg">
                  <h3 className="text-lg font-bold text-red-900 mb-3">Efficiency Breakdown</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Allocation → Issue Efficiency:</span>
                      <span className="font-bold text-red-700">{supplyChain?.efficiency?.issue_rate}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Issue → Sale Efficiency:</span>
                      <span className="font-bold text-red-700">{supplyChain?.efficiency?.sale_rate}%</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t-2 border-red-300">
                      <span className="font-semibold">Overall Efficiency:</span>
                      <span className="font-bold text-red-700 text-lg">{supplyChain?.efficiency?.overall_efficiency}%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Districts Tab */}
          <TabsContent value="districts">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Performers */}
              <Card>
                <CardHeader>
                  <CardTitle>Top 5 Performing Districts</CardTitle>
                  <CardDescription>Highest distribution rates</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {topBottom?.top_performers?.map((district, idx) => (
                      <div key={idx} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-gray-900">{district.district}</span>
                          <span className="text-lg font-bold text-green-600">{district.sale_pct}%</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
                          <div>
                            <p className="text-gray-500">AWCs</p>
                            <p className="font-medium text-gray-900">{district.anganwadi_count}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Issued</p>
                            <p className="font-medium text-gray-900">{district.issued_qty?.toLocaleString()} kg</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Sale</p>
                            <p className="font-medium text-gray-900">{district.sale_qty?.toLocaleString()} kg</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Bottom Performers */}
              <Card>
                <CardHeader>
                  <CardTitle>Bottom 5 Performing Districts</CardTitle>
                  <CardDescription>Need improvement in distribution</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {topBottom?.bottom_performers?.map((district, idx) => (
                      <div key={idx} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-gray-900">{district.district}</span>
                          <span className="text-lg font-bold text-orange-600">{district.sale_pct}%</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
                          <div>
                            <p className="text-gray-500">AWCs</p>
                            <p className="font-medium text-gray-900">{district.anganwadi_count}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Issued</p>
                            <p className="font-medium text-gray-900">{district.issued_qty?.toLocaleString()} kg</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Sale</p>
                            <p className="font-medium text-gray-900">{district.sale_qty?.toLocaleString()} kg</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Performance Distribution Tab */}
          <TabsContent value="performance">
            <Card>
              <CardHeader>
                <CardTitle>Performance Distribution Across Districts</CardTitle>
                <CardDescription>Distribution categories by performance metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={perfDistribution?.distribution || []}
                          dataKey="count"
                          nameKey="category"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={(entry) => `${entry.category}: ${entry.count}`}
                        >
                          {perfDistribution?.distribution?.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {perfDistribution?.distribution?.map((item, idx) => (
                      <div key={idx} className="p-4 rounded-lg border" style={{ backgroundColor: `${item.color}15`, borderColor: item.color }}>
                        <p className="text-3xl font-bold mb-2" style={{ color: item.color }}>{item.count}</p>
                        <p className="text-sm text-gray-700 font-medium">{item.category}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {((item.count / perfDistribution?.total_districts) * 100).toFixed(1)}% of districts
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Table Tab */}
          <TabsContent value="data">
            <Card>
              <CardHeader>
                <CardTitle>District-wise Complete Data</CardTitle>
                <CardDescription>Comprehensive HCM distribution metrics for all districts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">AWCs</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Allocated (kg)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Permit (kg)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Issued (kg)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sale (kg)</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Issue Rate</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Performance</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {districtDelivery.map((district, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{district.district}</td>
                          <td className="px-4 py-3 text-gray-700">{district.anganwadi_count}</td>
                          <td className="px-4 py-3 text-right text-gray-700">{district.allocated_qty?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-gray-700">{district.permit_qty?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-blue-600 font-medium">{district.issued_qty?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-gray-700">{district.sale_qty?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="font-semibold" style={{ color: getPerformanceColor(
                              district.allocated_qty > 0 ? (district.issued_qty / district.allocated_qty * 100) : 0
                            ) }}>
                              {district.allocated_qty > 0 ? ((district.issued_qty / district.allocated_qty * 100).toFixed(1)) : 0}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              district.performance_rating === 'Excellent' ? 'bg-green-100 text-green-800' :
                              district.performance_rating === 'Good' ? 'bg-yellow-100 text-yellow-800' :
                              district.performance_rating === 'Needs Improvement' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {district.performance_rating}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  </div>
  );
};

export default HCMDashboard;
